<template>
  <div class="wrapper">
    <div class="input" :style="inputColors">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <title>magnify</title>
        <path
          :fill="inputTextColor"
          d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
        />
      </svg>

      <input
        ref="input"
        v-model="query"
        :style="`color: ${inputTextColor};`"
        type="text"
        tabindex="0"
        @click="input.focus()"
        @keyup.enter="search"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ArtifactType } from '@/models'
  import { useArtifactCacheStore } from '@/stores/artifactCacheStore'
  import { useExplorerUiStore } from '@/stores/explorerUiStore'
  import { computed, onMounted, ref } from 'vue'

  // Due to the shadow dom the input would not get focused otherwise
  const input = ref()

  const query = ref('')

  const artifactType = ref<ArtifactType>()

  const artifactCacheStore = useArtifactCacheStore()
  const explorerUiStore = useExplorerUiStore()

  const props = defineProps({
    // Cannot pass the artifact type as an object
    artifactTypeId: { type: Number, required: true },
  })

  onMounted(() => {
    artifactType.value = artifactCacheStore.artifactTypes.find(
      (at) => at.id === props.artifactTypeId,
    )
    query.value = artifactType.value?.containerSearchQuery ?? ''
  })

  // Use consistent dark colors for visibility against semi-transparent white background
  const inputColors = computed((): string => {
    return 'background-color: rgba(255, 255, 255, 0.25); border-color: #424242;'
  })

  // Dark gray text color for visibility
  const inputTextColor = computed((): string => {
    return '#424242'
  })

  function search() {
    if (artifactType.value) {
      // Replaces EventBus 'searchArtifactsForArtifactType' event
      explorerUiStore.triggerArtifactTypeSearch(
        query.value,
        artifactType.value,
        explorerUiStore.searchMode,
      )
    }
  }
</script>

<style scoped lang="scss">
  div.wrapper {
    display: flex;
    justify-content: space-around;
    margin: 5px 10px 5px;
  }

  div.input {
    font-family: 'Roboto', sans-serif;
    width: 100%;
    border: 1px solid rgb(var(--v-theme-font-color));
    border-radius: 7px;
    padding: 7px 10px;
    box-sizing: border-box;
    font-size: 14px;
    display: flex;
    flex-direction: row;
  }

  input {
    background-color: transparent;
    border: none;
    flex-grow: 4;
  }

  input:hover,
  input:active,
  input:focus-visible {
    outline: none;
    border: none;
  }

  div.input > svg {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 20px;
    height: 100%;
  }
</style>
